import { BadgeList } from '@/components/badges'
import { useBadges } from '@/lib/verification'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { placeByCode } from '@/lib/search'
import { startAsClient } from '@/lib/messages'
import { useBookingActions, useMyBookings } from '@/lib/bookings'
import { BookingForm } from '@/components/booking-form'
import { errorText } from '@/lib/utils'
import { BK_STATUS } from './bookings'
import { ChevronLeft, MapPin, Calendar, Wallet } from 'lucide-react'
import { btnPrimary, btnSecondary, SkeletonRows, useConfirm } from '@/components/rm'
import { useAuth } from '@/hooks/use-auth'
import { useT } from '@/i18n'
import { fmtDate, fmtDateTime, phDay, pesoFromCentavos } from '@/lib/ph'
import { effectiveStatus, useMyTask, useTaskActions, useTaskPhotos } from '@/lib/tasks'
import { cn } from '@/lib/utils'
import { STATUS_KEY, STATUS_TONE } from './tasks'

// Spec 005 US3. One task, its offers, accept, cancel.
export const Route = createFileRoute('/_client/tasks_/$id')({ component: TaskDetail })

function TaskDetail() {
  const { id } = Route.useParams()
  const { t } = useT()
  const { user } = useAuth()
  const navigate = useNavigate()
  const q = useMyTask(id)
  const { accept, cancel } = useTaskActions(id)
  const [confirm, confirmDialog] = useConfirm()
  const [notice, setNotice] = useState('')
  const task = q.data?.task
  const bookings = useMyBookings()
  const { fromOffer } = useBookingActions()
  const photos = useTaskPhotos(user?.id ?? null, id, task?.photo_count ?? 0)
  const badges = useBadges((q.data?.offers ?? []).map((o) => o.slug))

  if (q.isLoading) return <SkeletonRows count={4} />
  if (!task) return <p className="text-[14px] text-token-secondary">{t('tasks.notFound')}</p>
  const st = effectiveStatus(task)
  const offers = q.data!.offers
  const accepted = offers.find((o) => o.status === 'accepted')
  const booking = (bookings.data ?? []).find((x) => x.task_id === task.id && !['cancelled', 'expired'].includes(x.status))

  return (
    <div className="space-y-4">
      {confirmDialog}
      <Link to="/tasks" className="inline-flex items-center gap-1 text-[13px] font-semibold text-token-secondary"><ChevronLeft className="h-4 w-4" />{t('tasks.back')}</Link>
      <section className="space-y-3 rounded-[16px] border border-token-subtle bg-token-surface p-4">
        <div className="flex items-start justify-between gap-3">
          <h1 className="font-display text-[20px] font-bold leading-tight text-token-primary">{task.title}</h1>
          <span className={cn('flex-none rounded-full px-2.5 py-0.5 text-[12px] font-semibold', STATUS_TONE[st])}>{t(STATUS_KEY[st])}</span>
        </div>
        {task.description && <p className="whitespace-pre-line text-[14.5px] text-token-primary">{task.description}</p>}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-token-secondary">
          <PlaceName code={task.place_code} />
          {task.budget_centavos != null && <span className="inline-flex items-center gap-1"><Wallet className="h-3.5 w-3.5" />{t('tasks.budgetLabel', { price: pesoFromCentavos(task.budget_centavos) })}</span>}
          {task.preferred_date && <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{t('tasks.on', { date: fmtDate(phDay(task.preferred_date)) })}</span>}
        </div>
        {(photos.data ?? []).length > 0 && (
          <div className="flex gap-2 overflow-x-auto">{photos.data!.map((u) => <img key={u} src={u} alt="" className="h-24 w-24 flex-none rounded-[10px] object-cover" />)}</div>
        )}
        {st === 'open' && <p className="text-[12px] text-token-muted">{t('tasks.expires', { date: fmtDateTime(task.expires_at) })}</p>}

      </section>

      {st === 'assigned' && accepted && (booking
        ? <Link to="/bookings/$id" params={{ id: booking.id }} className="block rounded-[16px] border border-token-success bg-token-success-surface px-4 py-3 text-[14px] font-semibold text-token-success">
            {t('bk.nav')}: {t(BK_STATUS[booking.status])} →
          </Link>
        : <section className="space-y-3 rounded-[16px] border border-token-subtle bg-token-surface p-4">
            <h2 className="text-[16px] font-bold text-token-primary">{t('bk.confirmTitle')}</h2>
            <p className="text-[13px] text-token-secondary">{t('bk.confirmHint', { business: accepted.business_name })}</p>
            <BookingForm priceCentavos={accepted.price_centavos} submitLabel={t('bk.confirm')} busyLabel={t('bk.requesting')} busy={fromOffer.isPending}
              initialPlace={null}
              onSubmit={async (bk) => {
                try {
                  const id = await fromOffer.mutateAsync({ offer: accepted.id, ...bk })
                  navigate({ to: '/bookings/$id', params: { id } })
                  return null
                } catch (e) { return /2 hours/.test(errorText(e, '')) ? t('bk.tooSoon') : t('bk.failed') }
              }} />
          </section>)}

      <section className="space-y-3">
        <h2 className="px-1 text-[13px] font-bold uppercase tracking-[0.06em] text-token-muted">{t('tasks.offers', { n: offers.filter((o) => o.status === 'active').length })}</h2>
        {offers.length === 0 && <p className="rounded-[16px] border border-token-subtle bg-token-surface p-4 text-[13.5px] text-token-secondary">{t('tasks.noOffers')}</p>}
        {notice && <p role="alert" className="text-[13px] text-token-danger">{notice}</p>}
        {offers.map((o) => (
          <article key={o.id} className={cn('rounded-[16px] border bg-token-surface p-4', o.status === 'accepted' ? 'border-token-success' : 'border-token-subtle', o.status === 'declined' && 'opacity-60')}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-[15px] font-bold text-token-primary">{o.business_name}</p>
                {o.headline && <p className="truncate text-[12.5px] text-token-secondary">{o.headline}</p>}
                <BadgeList b={badges.data?.get(o.slug ?? '')} className="mt-1" />
              </div>
              <span className="flex-none font-display text-[18px] font-bold tabular-nums text-token-primary">{pesoFromCentavos(o.price_centavos)}</span>
            </div>
            {o.message && <p className="mt-2 whitespace-pre-line text-[14px] text-token-primary">{o.message}</p>}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-3">
                {o.slug && <Link to="/p/$slug" params={{ slug: o.slug }} className="text-[13px] font-semibold text-brand hover:underline">{t('tasks.viewProfile')}</Link>}
                {o.slug && o.status !== 'declined' && <button type="button" className="text-[13px] font-semibold text-brand hover:underline"
                  onClick={async () => { const th = await startAsClient(o.slug!, task.id); navigate({ to: '/messages/$id', params: { id: th } }) }}>{t('msg.message')}</button>}
              </span>
              {o.status === 'accepted' && <span className="text-[13px] font-semibold text-token-success">{t('tasks.accepted')}</span>}
              {o.status === 'declined' && <span className="text-[13px] text-token-muted">{t('tasks.declined')}</span>}
              {o.status === 'active' && st === 'open' && (
                <button type="button" className={`${btnPrimary} h-9`} disabled={accept.isPending} onClick={async () => {
                  setNotice('')
                  const r = await accept.mutateAsync(o.id).catch(() => null)
                  if (r && !r.ok) setNotice(r.error === 'offer_withdrawn' ? t('tasks.withdrawn') : t('tasks.closed'))
                }}>{accept.isPending ? t('tasks.accepting') : t('tasks.accept')}</button>
              )}
            </div>
          </article>
        ))}
      </section>

      {st === 'open' && (
        <button type="button" className={`${btnSecondary} h-10 w-full justify-center`} onClick={async () => {
          const ok = await confirm({ title: t('tasks.cancel'), body: t('tasks.cancelConfirm'), confirmLabel: t('tasks.cancel'), cancelLabel: t('tasks.keep'), tone: 'danger' })
          if (ok) await cancel.mutateAsync()
        }}>{t('tasks.cancel')}</button>
      )}
    </div>
  )
}

function PlaceName({ code }: { code: string }) {
  const p = useQuery({ queryKey: ['place', code], staleTime: Infinity, queryFn: () => placeByCode(code) })
  return <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.data ? [p.data.name, p.data.parent].filter(Boolean).join(', ') : ''}</span>
}
